import React, { useEffect } from 'react';
import { Form, Input, Select, Checkbox, Button, Radio, DatePicker } from 'antd';
import { useSearchParams } from 'react-router-dom';
import CredPhoneInput from '../../components/CredPhoneInput';
import SpecialtyTypeSelect from 'components/form/SpecialtyTypeSelect';
import EhrSystemSelect from 'components/form/EhrSystemSelect';
import StateSelect from 'components/form/StateSelect';
import AutoAddressInput from 'components/AutoAddressInput';
import useSettings from 'hooks/useSettings';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import { EnumFields } from 'types/enumerationData';
import { ENPIType } from 'types/credentialing';
import SectionHeader from '../../components/SectionHeader';
import {
    PRACTICE_TYPES,
    CARE_DELIVERY,
    LOCATION_CARE_DELIVERY,
    SPECIALTY_EXTRA_OPTIONS,
    SPECIALTY_OTHERS,
    MONTHLY_VISITS_OPTIONS,
    PATIENT_PAYER_MIX_OPTIONS,
    selectOptions,
} from '../../constants';
import s from '../../s.module.less';

// 入口 NPI 类型 → Practice type 选项的默认映射
const NPI_TYPE_TO_PRACTICE_TYPE: Record<string, string> = {
    [ENPIType.PRACTICE_NPI]: 'Group practice',
    [ENPIType.INDIVIDUAL_NPI]: 'Solo / independent practice',
};

// 执业地址在表单里是数组 addressList，一个州一条；第 0 条是主地址（带
// 「Same as my contact address」开关），其余为后补的其它州地址。
const ADDR = 'addressList';

// 联系地址四项齐全才算「填过」——只有这样才谈得上「与联系地址相同」
const contactComplete = (c: any) => !!(c?.practiceAddress && c?.practiceCity && c?.practiceState && c?.practiceZip);

// 第一条执业地址是否等同于联系地址。勾选态由它实时推导，不存 state：
// 本步数据异步回填，后端地址一到就会自动取消勾选、解除字段禁用，
// 用户手改任一项同理。这样勾选态永远和输入框里的内容一致。
const sameAsContact = (row0: any, contact: any) =>
    contactComplete(contact) &&
    row0?.practiceAddress === contact.practiceAddress &&
    row0?.practiceCity === contact.practiceCity &&
    row0?.practiceState === contact.practiceState &&
    row0?.practiceZip === contact.practiceZip;

type AddrStateProps = {
    // Form.List field.name，即该行在 addressList 里的下标
    index: number;
    disabled?: boolean;
    placeholder: string;
};

// 一个州只填一条地址：本行的州下拉里把其它行已选走的州置灰。
// 需要随「其它行」的值变化重算，所以外面套一层 shouldUpdate（Form.List 本身只在增删行时重渲染）。
const AddressStateField = ({ index, disabled, placeholder }: AddrStateProps) => (
    <Form.Item noStyle shouldUpdate={(p, c) => p[ADDR] !== c[ADDR]}>
        {({ getFieldValue }) => {
            const list: any[] = getFieldValue(ADDR) || [];
            const taken = list
                .map((a, i) => (i === index ? undefined : a?.practiceState))
                .filter((v): v is string => !!v);
            return (
                <Form.Item
                    name={[index, 'practiceState']}
                    label="State"
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <StateSelect size="large" placeholder={placeholder} disabled={disabled} disableValues={taken} />
                </Form.Item>
            );
        }}
    </Form.Item>
);

export default function StepPractice() {
    const form = Form.useFormInstance();
    const [searchParams] = useSearchParams();
    const [setting] = useSettings();
    const [credentialInfo] = useCredentialInfo();
    const isGroupPractice = credentialInfo.billingNpiType?.value === ENPIType.PRACTICE_NPI;
    // EHR「Other」选项的 dict_key：ehrSystems 选中它时才展示自定义名称输入框
    const ehrOtherValue = React.useMemo(() => {
        const opts = setting[EnumFields.EHR_TYPE] || [];
        const other = opts.find(
            (o: any) =>
                String(o?.label ?? '')
                    .trim()
                    .toLowerCase() === 'other',
        );
        return other ? String(other.value) : '';
    }, [setting]);
    // 勾选时用联系地址回填第一条执业地址，取消时清空这四项。
    // 展开保留 list[0] 的其余字段（后端的 id / providerId 等元数据必须原样带回去，
    // 否则保存会被当成新增记录而不是修改现有那条）。其它州的地址行不动。
    const handleSameAddr = (checked: boolean) => {
        const contact = form.getFieldValue('contactAddress') || {};
        const list = [...(form.getFieldValue(ADDR) || [])];
        list[0] = {
            ...(list[0] || {}),
            practiceAddress: checked ? contact.practiceAddress : undefined,
            practiceCity: checked ? contact.practiceCity : undefined,
            practiceState: checked ? contact.practiceState : undefined,
            practiceZip: checked ? contact.practiceZip : undefined,
        };
        form.setFieldValue(ADDR, list);
    };

    useEffect(() => {
        // 接口未返回 practiceType 时自动填充：优先按入口 URL 的 type 映射；
        // group owner 代填（带 providerId、无 type 参数）时该 provider 属于 group practice
        if (!form.getFieldValue('practiceType')) {
            const mapped = NPI_TYPE_TO_PRACTICE_TYPE[searchParams.get('type') || ''];
            const fallback = searchParams.get('providerId') ? 'Group practice' : undefined;
            const next = mapped || fallback;
            if (next) form.setFieldValue('practiceType', next);
        }
        // 首次进入且第一条地址还空着时，用联系地址预填（联系地址填全了才预填）。
        // 勾选态本身不在这里定，由下面的 sameAsContact 按当前值实时推导——
        // 本步数据是异步回填的，本 effect 跑的时候接口可能还没回来，
        // 一旦把勾选态锁进 state，就会出现「勾着、字段灰着，里面却是后端地址」的假勾选。
        const row0 = (form.getFieldValue(ADDR) || [])[0] || {};
        const contact = form.getFieldValue('contactAddress') || {};
        const row0Empty = !row0.practiceAddress && !row0.practiceCity && !row0.practiceState && !row0.practiceZip;
        if (row0Empty && contactComplete(contact)) handleSameAddr(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 地址联想选中后 city/state/zip 三个回调同 tick 连续触发；逐个 setFieldValue 写叶子路径
    // 不会互相覆盖（不要基于整条数组重算后整体写回，那样会互相丢值）
    const patchFromSuggestion = (idx: number, field: string, val?: string) => {
        if (!val) return;
        form.setFieldValue([ADDR, idx, field], val);
    };

    return (
        <>
            <div className={s.wizSection}>
                <SectionHeader title="Practice location" />
                <Form.Item
                    name="practiceName"
                    label="Clinic / Practice name"
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <Input size="large" placeholder="e.g. Advanced Psychiatry Associates" />
                </Form.Item>
                <div className={s.wizRow}>
                    {/* 10/15：Medicare 855 要「执业开始年月」。group 是 practice 级的一个日期，
                        第一个 provider 填过后，后面的 provider 打开即预填（见向导 loadStep） */}
                    <Form.Item
                        name="startDate"
                        label="When did your practice start?"
                        rules={[{ required: true, message: 'Required' }]}
                        normalize={(v) => (v ? v.startOf('month') : v)}
                        extra={
                            isGroupPractice
                                ? 'One date for the practice. Fill it on the first provider and everyone you add after opens with it already filled in.'
                                : undefined
                        }
                    >
                        <DatePicker picker="month" size="large" format="MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="practicePhone"
                        label="Practice phone"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <CredPhoneInput size="large" placeholder="(555) 555-0100" />
                    </Form.Item>
                    {/* Practice type 由入口 solo/group（URL type）锁定，不可编辑 */}
                    <Form.Item name="practiceType" label="Practice type">
                        <Select size="large" disabled options={selectOptions(PRACTICE_TYPES)} />
                    </Form.Item>
                </div>
                <Form.List name={ADDR} initialValue={[{}]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, idx) => (
                                <Form.Item
                                    key={field.key}
                                    noStyle
                                    // 勾选态是从 addressList[0] 与 contactAddress 推导的，
                                    // 两者任一变化（含接口异步回填）都要重算
                                    shouldUpdate={(p, c) =>
                                        p[ADDR] !== c[ADDR] || p.contactAddress !== c.contactAddress
                                    }
                                >
                                    {({ getFieldValue }) => {
                                        const sameAddr =
                                            idx === 0 &&
                                            sameAsContact(
                                                (getFieldValue(ADDR) || [])[0],
                                                getFieldValue('contactAddress') || {},
                                            );
                                        return (
                                            <div className={idx > 0 ? `${s.wizEntry} ${s.entryDivider}` : s.wizEntry}>
                                                {idx === 0 ? (
                                                    <Form.Item>
                                                        <Checkbox
                                                            checked={sameAddr}
                                                            onChange={(e) => handleSameAddr(e.target.checked)}
                                                        >
                                                            Same as my contact address
                                                        </Checkbox>
                                                    </Form.Item>
                                                ) : (
                                                    <div className={s.entryRow}>
                                                        <div className={s.entryTitle}>Address {idx + 1}</div>
                                                        <button
                                                            type="button"
                                                            className={s.refRemove}
                                                            onClick={() => remove(field.name)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                                {/* 第一条沿用原布局（Address 整行 + City/State/ZIP 三列）；
                                        后补的地址先选州再填街道（州决定这条地址归属哪个 panel） */}
                                                {idx === 0 ? (
                                                    <>
                                                        <Form.Item
                                                            name={[field.name, 'practiceAddress']}
                                                            label="Address"
                                                            rules={[{ required: true, message: 'Required' }]}
                                                        >
                                                            <AutoAddressInput
                                                                size="large"
                                                                placeholder="Street address"
                                                                disabled={sameAddr}
                                                                displayFields={['street']}
                                                                onCityChange={(v) =>
                                                                    patchFromSuggestion(field.name, 'practiceCity', v)
                                                                }
                                                                onStateChange={(v) =>
                                                                    patchFromSuggestion(field.name, 'practiceState', v)
                                                                }
                                                                onPostcodeChange={(v) =>
                                                                    patchFromSuggestion(field.name, 'practiceZip', v)
                                                                }
                                                            />
                                                        </Form.Item>
                                                        <div className={s.wizRow}>
                                                            <Form.Item
                                                                name={[field.name, 'practiceCity']}
                                                                label="City"
                                                                rules={[{ required: true, message: 'Required' }]}
                                                            >
                                                                <Input
                                                                    size="large"
                                                                    placeholder="City"
                                                                    disabled={sameAddr}
                                                                />
                                                            </Form.Item>
                                                            <AddressStateField
                                                                index={field.name}
                                                                disabled={sameAddr}
                                                                placeholder="Select state"
                                                            />
                                                            <Form.Item
                                                                name={[field.name, 'practiceZip']}
                                                                label="ZIP code"
                                                                rules={[{ required: true, message: 'Required' }]}
                                                            >
                                                                <Input
                                                                    size="large"
                                                                    placeholder="ZIP code"
                                                                    disabled={sameAddr}
                                                                />
                                                            </Form.Item>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className={s.wizRow}>
                                                            <AddressStateField
                                                                index={field.name}
                                                                placeholder="Select a state"
                                                            />
                                                            <Form.Item
                                                                name={[field.name, 'practiceAddress']}
                                                                label="Address"
                                                                rules={[{ required: true, message: 'Required' }]}
                                                            >
                                                                <AutoAddressInput
                                                                    size="large"
                                                                    placeholder="Street address"
                                                                    displayFields={['street']}
                                                                    onCityChange={(v) =>
                                                                        patchFromSuggestion(
                                                                            field.name,
                                                                            'practiceCity',
                                                                            v,
                                                                        )
                                                                    }
                                                                    onStateChange={(v) =>
                                                                        patchFromSuggestion(
                                                                            field.name,
                                                                            'practiceState',
                                                                            v,
                                                                        )
                                                                    }
                                                                    onPostcodeChange={(v) =>
                                                                        patchFromSuggestion(
                                                                            field.name,
                                                                            'practiceZip',
                                                                            v,
                                                                        )
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </div>
                                                        <div className={s.wizRow}>
                                                            <Form.Item
                                                                name={[field.name, 'practiceCity']}
                                                                label="City"
                                                                rules={[{ required: true, message: 'Required' }]}
                                                            >
                                                                <Input size="large" placeholder="City" />
                                                            </Form.Item>
                                                            <Form.Item
                                                                name={[field.name, 'practiceZip']}
                                                                label="ZIP code"
                                                                rules={[{ required: true, message: 'Required' }]}
                                                            >
                                                                <Input size="large" placeholder="ZIP code" />
                                                            </Form.Item>
                                                        </div>
                                                    </>
                                                )}
                                                <Form.Item
                                                    name={[field.name, 'careDelivery']}
                                                    label="Services provided at this location"
                                                    rules={[{ required: true, message: 'Required' }]}
                                                >
                                                    <Radio.Group
                                                        className={s.svcTypeGroup}
                                                        options={LOCATION_CARE_DELIVERY}
                                                    />
                                                </Form.Item>
                                            </div>
                                        );
                                    }}
                                </Form.Item>
                            ))}
                            <Button block onClick={() => add({})} className={s.refAdd}>
                                + Add another practice address
                            </Button>
                        </>
                    )}
                </Form.List>
            </div>

            {/* D0（10/26）：团队据此判断先做哪些 practice。group 是 practice 级，第一个 provider 填过后，
                后面的 provider 打开即预填（见向导 loadStep，同 Practice start） */}
            <div className={s.wizSection}>
                <SectionHeader title="Your patients" />
                {isGroupPractice && (
                    <p className={s.patientsHint}>
                        For the whole practice. Fill it on the first provider and everyone you add after opens with it
                        already filled in.
                    </p>
                )}
                <Form.Item
                    name="monthlyVisits"
                    label="About how many visits does your practice bill each month?"
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <Select size="large" placeholder="Select..." options={MONTHLY_VISITS_OPTIONS} />
                </Form.Item>
                <Form.Item
                    name="patientPayerMix"
                    label="How do most of your patients pay today?"
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <Select size="large" placeholder="Select..." options={PATIENT_PAYER_MIX_OPTIONS} />
                </Form.Item>
            </div>

            <div className={s.wizSection}>
                <SectionHeader title="Specialties" />
                <Form.Item
                    name="specialtyOffered"
                    label="Specialty offered"
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <SpecialtyTypeSelect
                        size="large"
                        mode="multiple"
                        placeholder="Select specialties"
                        extraOptions={SPECIALTY_EXTRA_OPTIONS}
                    />
                </Form.Item>
                {/* 选中 Others 时补填具体专科；提交时用它替换掉 "Others" 本身（见 stepValuesToDto） */}
                <Form.Item noStyle shouldUpdate={(p, c) => p.specialtyOffered !== c.specialtyOffered}>
                    {({ getFieldValue }) => {
                        const specialties: string[] = getFieldValue('specialtyOffered') || [];
                        if (!specialties.includes(SPECIALTY_OTHERS)) return null;
                        return (
                            <Form.Item
                                name="specialtyOfferedOther"
                                label="Which specialty? Please specify"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input size="large" placeholder="Enter the specialty" />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
                <Form.Item name="careDelivery" label="Care delivery" rules={[{ required: true, message: 'Required' }]}>
                    <Select size="large" placeholder="Select" options={selectOptions(CARE_DELIVERY)} />
                </Form.Item>
            </div>

            <div className={s.wizSection}>
                <SectionHeader title="EHR" />
                <Form.Item name="ehrSystems" label="Which EHR does your practice currently use?">
                    <EhrSystemSelect size="large" />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(p, c) => p.ehrSystems !== c.ehrSystems}>
                    {({ getFieldValue }) => {
                        const ehr: string[] = getFieldValue('ehrSystems') || [];
                        const showOther = !!ehrOtherValue && ehr.includes(ehrOtherValue);
                        if (!showOther) return null;
                        return (
                            <Form.Item
                                name="ehrSystemsOther"
                                label="Which EHR? Please specify"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input size="large" placeholder="Enter the EHR name" />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>
        </>
    );
}
